import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// --- Animated Count-Up Hook ---
export function useCountUp(target, duration = 1500, startOnMount = true) {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!startOnMount) return;
    
    // Only animate if target is a valid positive number
    if (typeof target !== 'number' || target <= 0) {
      setValue(0);
      return;
    }
    
    // Cancel any existing animation
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    
    // Reset value to 0 before starting animation
    setValue(0);
    
    const start = performance.now();
    const currentTarget = target; // Capture the target value
    
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for satisfying deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(currentTarget * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [target, duration, startOnMount]);

  return value;
}

// --- Animated Currency Display ---
export function AnimatedCurrency({ amount, className, prefix = "£" }) {
  const animated = useCountUp(amount, 1800);
  const formatted = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(animated);
  return <span className={className}>{prefix}{formatted}</span>;
}

// --- Animated Score Display ---
export function AnimatedScore({ score, max = 100, className }) {
  const animated = useCountUp(score, 1400);
  return <span className={className}>{animated}/{max}</span>;
}

// --- Real-Time Cost Ticker ---
export function CostTicker({ annualCost }) {
  const [elapsed, setElapsed] = useState(0);
  const costPerSecond = annualCost / (365.25 * 24 * 3600);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed((Date.now() - startRef.current) / 1000);
    }, 50);
    return () => clearInterval(timer);
  }, []);

  const costSoFar = (costPerSecond * elapsed).toFixed(2);
  const costPerDay = (annualCost / 365.25).toFixed(0);
  const costPerWeek = (annualCost / 52).toFixed(0);

  return (
    <div className="bg-slate-900 text-white rounded-xl p-6 mb-8" data-testid="cost-ticker" style={{backgroundColor: '#333333'}}>
      <div className="text-center">
        <p className="text-slate-400 text-sm mb-1">Since you opened this page, poor culture has cost your organisation</p>
        <div className="text-3xl font-mono font-bold text-red-400 mb-3 tabular-nums">
          £{costSoFar}
        </div>
        <div className="flex justify-center gap-8 text-sm">
          <div>
            <span className="text-slate-400">Per day: </span>
            <span className="font-semibold" style={{color: '#FFDD00'}}>£{Number(costPerDay).toLocaleString('en-GB')}</span>
          </div>
          <div>
            <span className="text-slate-400">Per week: </span>
            <span className="font-semibold" style={{color: '#FFDD00'}}>£{Number(costPerWeek).toLocaleString('en-GB')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Benchmark Indicator ---
export function BenchmarkBar({ label, yourValue, avgValue, unit = "", lowerIsBetter = true, formatFn }) {
  const diff = yourValue - avgValue;
  const pctDiff = avgValue > 0 ? Math.abs(diff / avgValue * 100).toFixed(0) : 0;
  const isBetter = lowerIsBetter ? diff <= 0 : diff >= 0;
  const displayYours = formatFn ? formatFn(yourValue) : `${yourValue}${unit}`;
  const displayAvg = formatFn ? formatFn(avgValue) : `${avgValue}${unit}`;

  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-700">{label}</div>
        <div className="text-xs text-slate-500">Industry avg: {displayAvg}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-800">{displayYours}</span>
        {diff !== 0 && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            isBetter
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {isBetter ? '▼' : '▲'} {pctDiff}% {isBetter ? 'better' : 'worse'}
          </span>
        )}
        {diff === 0 && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">= average</span>
        )}
      </div>
    </div>
  );
}

// --- Improvement Simulator (Culture) ---
export function CultureImprovementSimulator({ currentLeavers, costPerLeaver, currentSickDays, dailySalary, annualCost }) {
  const [reduceLeavers, setReduceLeavers] = useState(Math.max(1, Math.round(currentLeavers * 0.3)));
  const [reduceSickDays, setReduceSickDays] = useState(Math.max(1, Math.round(currentSickDays * 0.2)));

  const turnoverSaving = reduceLeavers * costPerLeaver;
  const absenceSaving = reduceSickDays * dailySalary;
  const totalSaving = turnoverSaving + absenceSaving;
  const newCost = Math.max(0, annualCost - totalSaving);
  const pctReduction = annualCost > 0 ? ((totalSaving / annualCost) * 100).toFixed(0) : 0;

  const fmt = (v) => '£' + Number(v.toFixed(0)).toLocaleString('en-GB');

  return (
    <div className="bg-white border-2 border-dashed border-blue-200 rounded-xl p-6 mb-8" data-testid="improvement-simulator">
      <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">What If? Improvement Simulator</h3>
      <p className="text-sm text-slate-500 text-center mb-6">Drag the sliders to see potential savings</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Reduce leavers by <strong className="text-blue-600">{reduceLeavers}</strong> people
          </label>
          <input type="range" min={0} max={currentLeavers} value={reduceLeavers}
            onChange={(e) => setReduceLeavers(parseInt(e.target.value))}
            className="w-full slider" />
          <div className="flex justify-between text-xs text-slate-400 mt-1"><span>0</span><span>{currentLeavers}</span></div>
          <div className="text-sm text-green-600 font-medium mt-2">Saves {fmt(turnoverSaving)}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Reduce sick days by <strong className="text-blue-600">{reduceSickDays}</strong> days
          </label>
          <input type="range" min={0} max={currentSickDays} value={reduceSickDays}
            onChange={(e) => setReduceSickDays(parseInt(e.target.value))}
            className="w-full slider" />
          <div className="flex justify-between text-xs text-slate-400 mt-1"><span>0</span><span>{currentSickDays}</span></div>
          <div className="text-sm text-green-600 font-medium mt-2">Saves {fmt(absenceSaving)}</div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
        <div className="text-sm text-green-700 mb-1">Projected annual cost after improvements</div>
        <div className="text-3xl font-bold text-green-700">{fmt(newCost)}</div>
        <div className="text-sm text-green-600 mt-1">That's a <strong>{pctReduction}%</strong> reduction — saving {fmt(totalSaving)} per year</div>
      </div>
    </div>
  );
}

// --- Improvement Simulator (Productivity) ---
export function ProductivityImprovementSimulator({ results, formData }) {
  const [improveSkills, setImproveSkills] = useState(10);
  const [reduceReactive, setReduceReactive] = useState(10);
  const [reduceSickDays, setReduceSickDays] = useState(Math.max(1, Math.round(formData.absence_days * 0.2)));

  // Rough cost model: each % improvement in skills reduces underperformance cost proportionally
  const baseDrag = results.estimated_productivity_drag;
  const skillsSaving = (improveSkills / 100) * (results.cost_breakdown.underperformance_cost + results.cost_breakdown.competence_delay_cost);
  const reactiveSaving = (reduceReactive / 100) * results.cost_breakdown.underperformance_cost * 0.5;
  const absenceSaving = reduceSickDays * (results.cost_breakdown.absence_cost / Math.max(1, formData.absence_days));
  const totalSaving = skillsSaving + reactiveSaving + absenceSaving;
  const newDrag = Math.max(0, baseDrag - totalSaving);
  const pctReduction = baseDrag > 0 ? ((totalSaving / baseDrag) * 100).toFixed(0) : 0;

  const fmt = (v) => '£' + Number(Math.round(v)).toLocaleString('en-GB');

  return (
    <div className="bg-white border-2 border-dashed border-blue-200 rounded-xl p-6 mb-8" data-testid="prod-improvement-simulator">
      <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">What If? Improvement Simulator</h3>
      <p className="text-sm text-slate-500 text-center mb-6">Drag the sliders to see how changes reduce your productivity drag</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Increase skilled staff by <strong className="text-blue-600">{improveSkills}%</strong>
          </label>
          <input type="range" min={0} max={Math.min(50, 100 - formData.fully_skilled_percentage)} value={improveSkills}
            onChange={(e) => setImproveSkills(parseInt(e.target.value))}
            className="w-full slider" />
          <div className="text-sm text-green-600 font-medium mt-2">Saves {fmt(skillsSaving)}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Reduce reactive work by <strong className="text-blue-600">{reduceReactive}%</strong>
          </label>
          <input type="range" min={0} max={formData.reactive_work_percentage} value={reduceReactive}
            onChange={(e) => setReduceReactive(parseInt(e.target.value))}
            className="w-full slider" />
          <div className="text-sm text-green-600 font-medium mt-2">Saves {fmt(reactiveSaving)}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Reduce absence by <strong className="text-blue-600">{reduceSickDays}</strong> days
          </label>
          <input type="range" min={0} max={formData.absence_days} value={reduceSickDays}
            onChange={(e) => setReduceSickDays(parseInt(e.target.value))}
            className="w-full slider" />
          <div className="text-sm text-green-600 font-medium mt-2">Saves {fmt(absenceSaving)}</div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
        <div className="text-sm text-green-700 mb-1">Projected productivity drag after improvements</div>
        <div className="text-3xl font-bold text-green-700">{fmt(newDrag)}</div>
        <div className="text-sm text-green-600 mt-1">That's a <strong>{pctReduction}%</strong> reduction — saving {fmt(totalSaving)} per year</div>
      </div>
    </div>
  );
}


// --- Culture League Table ---
export function CultureLeagueTable({ industry, totalCost }) {
  const [ranking, setRanking] = useState(null);

  useEffect(() => {
    if (!industry || !totalCost) return;
    axios.get(`${API}/ranking/culture`, { params: { industry, total_cost: totalCost } })
      .then(res => { if (res.data.has_ranking) setRanking(res.data); })
      .catch(() => {});
  }, [industry, totalCost]);

  if (!ranking) return null;

  const fmt = (v) => '£' + Number(v).toLocaleString('en-GB');
  const betterThanMost = ranking.percentile_better >= 50;
  const industryLabel = industry === 'transport' ? 'Transport & Logistics' : industry.charAt(0).toUpperCase() + industry.slice(1);

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-6 mb-8" data-testid="culture-league-table">
      <h3 className="text-lg font-bold text-slate-800 mb-4">Industry League Table</h3>
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Rank circle */}
        <div className="flex-shrink-0">
          <div className={`w-28 h-28 rounded-full flex flex-col items-center justify-center border-4 ${
            betterThanMost ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'
          }`}>
            <div className={`text-3xl font-bold ${betterThanMost ? 'text-green-700' : 'text-red-700'}`}>
              {betterThanMost ? `Top ${100 - ranking.percentile_better}%` : `Top ${100 - ranking.percentile_better}%`}
            </div>
          </div>
        </div>
        {/* Details */}
        <div className="flex-1 text-center md:text-left">
          <p className="text-slate-700 text-base mb-3">
            {betterThanMost ? (
              <>Your culture costs are <strong className="text-green-700">lower than {ranking.percentile_better}%</strong> of {industryLabel} organisations who've taken this assessment.</>
            ) : (
              <>Your culture costs are <strong className="text-red-700">higher than {100 - ranking.percentile_better}%</strong> of {industryLabel} organisations who've taken this assessment.</>
            )}
          </p>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm">
            <div className="bg-white rounded-lg px-4 py-2 border border-slate-200">
              <span className="text-slate-500">Your cost: </span>
              <span className="font-semibold text-slate-800">{fmt(ranking.your_cost)}</span>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 border border-slate-200">
              <span className="text-slate-500">Industry avg: </span>
              <span className="font-semibold text-slate-800">{fmt(ranking.industry_avg_cost)}</span>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 border border-slate-200">
              <span className="text-slate-500">Based on: </span>
              <span className="font-semibold text-slate-800">{ranking.total_in_industry} organisations</span>
            </div>
          </div>
        </div>
      </div>
      {/* Position bar */}
      <div className="mt-5">
        <div className="relative h-3 rounded-full bg-gradient-to-r from-green-400 via-amber-300 to-red-400 overflow-visible">
          <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-3 border-slate-800 rounded-full shadow-md -translate-x-1/2 transition-all duration-1000"
            style={{ left: `${Math.min(98, Math.max(2, 100 - ranking.percentile_better))}%`, borderWidth: '3px' }}></div>
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>Lowest costs</span><span>Highest costs</span>
        </div>
      </div>
    </div>
  );
}

// --- Productivity League Table ---
export function ProductivityLeagueTable({ fragilityScore }) {
  const [ranking, setRanking] = useState(null);

  useEffect(() => {
    if (fragilityScore === undefined || fragilityScore === null) return;
    axios.get(`${API}/ranking/productivity`, { params: { fragility_score: fragilityScore } })
      .then(res => { if (res.data.has_ranking) setRanking(res.data); })
      .catch(() => {});
  }, [fragilityScore]);

  if (!ranking) return null;

  const betterThanMost = ranking.percentile_better >= 50;

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-6 mb-8" data-testid="productivity-league-table">
      <h3 className="text-lg font-bold text-slate-800 mb-4">How You Rank</h3>
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="flex-shrink-0">
          <div className={`w-28 h-28 rounded-full flex flex-col items-center justify-center border-4 ${
            betterThanMost ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'
          }`}>
            <div className={`text-3xl font-bold ${betterThanMost ? 'text-green-700' : 'text-red-700'}`}>
              Top {100 - ranking.percentile_better}%
            </div>
          </div>
        </div>
        <div className="flex-1 text-center md:text-left">
          <p className="text-slate-700 text-base mb-3">
            {betterThanMost ? (
              <>Your fragility score is <strong className="text-green-700">lower than {ranking.percentile_better}%</strong> of organisations who've taken this diagnostic.</>
            ) : (
              <>Your fragility score is <strong className="text-red-700">higher than {100 - ranking.percentile_better}%</strong> of organisations who've taken this diagnostic.</>
            )}
          </p>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm">
            <div className="bg-white rounded-lg px-4 py-2 border border-slate-200">
              <span className="text-slate-500">Your score: </span>
              <span className="font-semibold text-slate-800">{ranking.your_score}/100</span>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 border border-slate-200">
              <span className="text-slate-500">Average score: </span>
              <span className="font-semibold text-slate-800">{ranking.avg_fragility_score}/100</span>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 border border-slate-200">
              <span className="text-slate-500">Based on: </span>
              <span className="font-semibold text-slate-800">{ranking.total_submissions} organisations</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-5">
        <div className="relative h-3 rounded-full bg-gradient-to-r from-green-400 via-amber-300 to-red-400 overflow-visible">
          <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-3 border-slate-800 rounded-full shadow-md -translate-x-1/2 transition-all duration-1000"
            style={{ left: `${Math.min(98, Math.max(2, 100 - ranking.percentile_better))}%`, borderWidth: '3px' }}></div>
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>Lowest risk</span><span>Highest risk</span>
        </div>
      </div>
    </div>
  );
}
